// React
import React, { useState, useCallback, memo, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'
// Components
import BBundleItems from './styles'
import FocalImage from 'ui/components/FocalImage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown as farChevronDown } from '@fortawesome/pro-regular-svg-icons'

// Libraries
import _sortBy from 'lodash/sortBy'

// Shared
import { updateConfiguration } from 'shop-components/redux/actions/item'
import NO_PRODUCT_IMAGE from '@assets/images/no-product-image.png'
import { withTranslation } from 'shared/utils/withTranslation'
import { hasVariations } from 'shop-components/utils/variations'

const getItem = (item, selected) => {
  const variation = item.product_group?.products?.find((p) => p.id === selected)
  const product = item.product_group?.products?.find((p) => p.id === item.product_id)
  const productGroup = item.product_group

  return variation || product || productGroup
}

const BundleItem = ({ productId, cart, bundleItem, updateConfiguration, t, tOrEmptyString, bundleError }) => {
  const [selected, setSelected] = useState(bundleItem.product_id || '')
  const item = getItem(bundleItem, selected)
  const variations = _sortBy(
    bundleItem.product_group?.products?.filter(({ archived_at: archivedAt }) => !archivedAt),
    'sorting_weight'
  )
  const photo = variations.find((p) => p.id === selected)?.photo || { url: item.photo_url }

  useEffect(() => {
    if (bundleItem.product_id && cart?.id) {
      updateConfiguration({
        id: productId,
        itemId: bundleItem.id,
        variationId: bundleItem.product_id
      })
    }
  }, [bundleItem.product_id, productId, bundleItem.id, cart?.id])

  const handleSetSelected = useCallback(
    (id) => {
      if (cart?.id && id !== selected) {
        setSelected(id)
        updateConfiguration({ id: productId, itemId: bundleItem.id, variationId: id })
      }
    },
    [selected, productId, bundleItem.id, cart]
  )

  const showError = useMemo(() => bundleError && selected === '', [bundleError, selected])

  return (
    <BBundleItems.BundleItem key={bundleItem.id}>
      <BBundleItems.BundleItemCell part="bundle-item__image">
        <FocalImage url={photo?.url || NO_PRODUCT_IMAGE} preview={photo?.preview} coordinates={photo?.coordinates} />
      </BBundleItems.BundleItemCell>
      <BBundleItems.BundleItemCell>{`${bundleItem.quantity}x`}</BBundleItems.BundleItemCell>
      <BBundleItems.BundleItemCell>
        {(!hasVariations(bundleItem.product_group) || bundleItem.product_id) && item.name}
        {hasVariations(bundleItem.product_group) && !bundleItem.product_id && (
          <BBundleItems.BundleItemSelectWrapper>
            <BBundleItems.BundleItemSelect
              className="bundle-item__select"
              name={`variation-${bundleItem.id}`}
              id={`variation-${bundleItem.id}`}
              value={selected}
              error={showError}
              onChange={({ target }) => handleSetSelected(target.value)}
              required
            >
              <option value="" disabled>{tOrEmptyString('user.store.select_variation_placeholder')}</option>
              {variations.map((variation) => (
                <option key={variation.id} value={variation.id}>
                  {variation.name}
                </option>
              ))}
            </BBundleItems.BundleItemSelect>
            <FontAwesomeIcon icon={farChevronDown} />
          </BBundleItems.BundleItemSelectWrapper>
        )}
      </BBundleItems.BundleItemCell>
      {showError && <BBundleItems.BundleItemSelectError>{t('user.store.select_variation_error')}</BBundleItems.BundleItemSelectError>}
    </BBundleItems.BundleItem>
  )
}

BundleItem.propTypes = {
  productId: PropTypes.string,
  cart: PropTypes.object,
  bundleItem: PropTypes.object,
  updateConfiguration: PropTypes.func,
  bundleError: PropTypes.bool,
  t: PropTypes.func,
  tOrEmptyString: PropTypes.func
}

const mapStateToProps = (state, props) => ({
  cart: state.orm.carts.getById(state.client.cartId ?? window.booqableOptions.cart?.id),
  bundleError: state.items.records[props.productId]?.variant_error
})

const mapDispatchToProps = (dispatch, _props) => ({
  updateConfiguration: (attributes) => dispatch(updateConfiguration(attributes))
})

const decoratedComponent = compose(
  withTranslation('user'),
  reduxConnect(mapStateToProps, mapDispatchToProps)
)(BundleItem)

export default memo(decoratedComponent)
